
import React from 'react';
import { LucideIcon, Check } from 'lucide-react';
import { createGlobalStyle } from 'styled-components';

const ItemAnimationStyles = createGlobalStyle`
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(2px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .breakdown-item {
    opacity: 0;
    animation: fadeIn 0.2s ease-out forwards;
  }
  
  .savings-card-green:hover {
    background-color: rgba(220, 252, 231, 0.9); /* bg-green-100 */
    border-color: #4ade80; /* border-green-400 */
  }
  
  .savings-card-blue:hover {
    background-color: rgba(219, 234, 254, 0.9); /* bg-blue-100 */
    border-color: #60a5fa; /* border-blue-400 */
  }
  
  .savings-card-purple:hover {
    background-color: rgba(237, 233, 254, 0.9); /* bg-purple-100 */
    border-color: #a78bfa; /* border-purple-400 */
  }
  
  .savings-card-amber:hover {
    background-color: rgba(254, 243, 199, 0.9); /* bg-amber-100 */
    border-color: #fbbf24; /* border-amber-400 */
  }
  
  .savings-card-red:hover {
    background-color: rgba(254, 226, 226, 0.9); /* bg-red-100 */
    border-color: #f87171; /* border-red-400 */
  }
  
  .savings-card-indigo:hover {
    background-color: rgba(224, 231, 255, 0.9); /* bg-indigo-100 */
    border-color: #818cf8; /* border-indigo-400 */
  }
  
  .savings-card-gray:hover {
    background-color: rgba(243, 244, 246, 0.9); /* bg-gray-100 */
    border-color: #9ca3af; /* border-gray-400 */
  }
`;

interface SavingsMetricCardProps {
  title: string;
  value: string | number;
  secondaryValue?: string | number;
  description?: string;
  detailedBreakdown?: string[];
  icon: LucideIcon;
  iconColor: string;
  bgColor: string;
  textColor: string;
  id: string;
  active?: boolean;
  expandable?: boolean; // New prop to control expand feature
}

const SavingsMetricCard: React.FC<SavingsMetricCardProps> = ({
  title,
  value,
  secondaryValue,
  description,
  detailedBreakdown,
  icon: Icon,
  iconColor,
  bgColor,
  textColor,
  id,
  active = false,
  expandable = true
}) => {
  // Extract color from bgColor (e.g., "bg-green-100" -> "green")
  const colorName = bgColor.split('-')[1] || "gray";
  
  return (
    <>
    <ItemAnimationStyles />
    <div 
      className={`savings-card bg-white p-2 rounded-lg shadow-sm border transition-all duration-300 group hover:shadow-md transform-gpu z-10 hover:z-20 cursor-pointer hover:-translate-y-0.5 savings-card-${colorName} ${active ? 'ring-2 ring-emerald-300 ring-opacity-50' : ''}`}
      id={id}
      data-active={active}
    >
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-1.5">
          <div className={`${bgColor} rounded-full p-1 ${iconColor}`}>
            <Icon className="h-5 w-5" />
          </div>
          <span className="text-gray-700 text-lg font-medium truncate max-w-[80px]">{title}</span>
        </div>
        <div className={`text-lg font-semibold ${textColor}`}>{value}</div>
      </div>
      
      {secondaryValue && (
        <div className="mt-1 flex justify-end">
          <div className="text-[12px] text-gray-500 bg-gray-50 px-1.5 py-0.5 rounded-full">
            {secondaryValue}
          </div>
        </div>
      )}
      
      {detailedBreakdown && detailedBreakdown.length > 0 && (
        <div className="mt-1">
          <div className={expandable 
            ? "overflow-hidden max-h-0 group-hover:max-h-20 transition-all duration-200 ease-in-out" 
            : "overflow-hidden max-h-20"}>
            <ul className="text-[10px] space-y-0.5 pt-1 mt-1">
              {detailedBreakdown.slice(0, 3).map((item, index) => (
                <li 
                  key={index} 
                  className={`flex items-start ${expandable ? "breakdown-item" : ""}`}
                  style={expandable ? { animationDelay: `${index * 50}ms` } : {}}
                >
                  <div className="flex-shrink-0 mt-0.5">
                    <Check className={`h-2 w-2 mr-1 ${iconColor}`} />
                  </div>
                  <span className={`text-gray-700 truncate text-[9px]`}>{item}</span>
                </li>
              ))}
              {detailedBreakdown.length > 3 && (
                <li className="text-[8px] text-gray-400 pl-3">
                  +{detailedBreakdown.length - 3} more
                </li>
              )}
            </ul>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default SavingsMetricCard;
